import { useEffect, useMemo, useRef, useState } from 'react'
import {
  AlertTriangle,
  CheckCircle2,
  Clock3,
  FileStack,
  ListOrdered,
  Trash2,
} from 'lucide-react'
import { DocumentUpload } from '@/components/app/DocumentUpload'
import { CBP7501Editor } from '@/features/case-workspace/CBP7501Editor'
import { applyCbp7501DocumentAutomation, applyCbp7501LineAutomation } from '@/features/case-workspace/cbp7501LineAutomation'
import { CommercialInvoiceEditor } from '@/features/case-workspace/CommercialInvoiceEditor'
import { Manifest1302Editor } from '@/features/case-workspace/Manifest1302Editor'
import { CBP_7501_LINE_ITEM_FIELDS } from '@/features/case-workspace/cbp7501'
import { COMMERCIAL_INVOICE_LINE_ITEM_FIELDS } from '@/features/case-workspace/invoiceTemplate'
import { MANIFEST_1302_SHIPMENT_FIELDS } from '@/features/case-workspace/manifest1302'
import { summarizeValidationIssues } from '@/features/case-workspace/mockData'
import {
  filterVisibleSourceDocuments,
  filterVisibleStructuredDocuments,
  MVP_VISIBLE_DOCUMENT_TYPES,
} from '@/features/case-workspace/mvpVisibility'
import { DOCUMENT_DEFINITIONS } from '@/features/case-workspace/schemas'
import { caseWorkspaceService } from '@/features/case-workspace/service'
import type {
  DocumentFieldDefinition,
  DocumentFieldState,
  DocumentWorkspaceFocusTarget,
  SourceDocumentRecord,
  StructuredDocumentRecord,
  SupportedDocumentType,
} from '@/features/case-workspace/types'
import type { CaseEvent } from '@/lib/cases'
import { formatDateTime } from '@/lib/cases'
import {
  FieldControl,
  InlineHelp,
  MetadataItem,
  ReadinessOverviewCard,
  SectionHeading,
  StatusPill,
  toneForDocumentStatus,
  toneForRecommendationState,
  toneForSourceAutomation,
} from '@/features/case-workspace/WorkspaceShared'
import { summarizeSectionStatus } from '@/features/case-workspace/utils'

type DocumentsWorkspaceTabProps = {
  caseId: string
  actorId?: string
  actorLabel: string
  sourceDocuments: SourceDocumentRecord[]
  structuredDocuments: StructuredDocumentRecord[]
  visibleDocumentTypes?: SupportedDocumentType[]
  showUploadArea?: boolean
  focusTarget: DocumentWorkspaceFocusTarget | null
  onUploaded: () => void | Promise<void>
  onSourceDocumentsChange: (
    nextSourceDocuments: SourceDocumentRecord[],
    nextStructuredDocuments?: StructuredDocumentRecord[],
    removedIds?: string[],
  ) => void
  onStructuredDocumentsChange: (records: StructuredDocumentRecord[]) => void
  onActivityEvent: (event: CaseEvent) => void
  onFocusHandled: () => void
}

type NavigatorTarget =
  | { kind: 'source'; id: string }
  | { kind: 'structured'; id: string }

function buildEmptyFieldState(actorLabel: string): DocumentFieldState {
  return {
    value: '',
    displayValue: '',
    origin: 'manual',
    reviewStatus: 'manually_entered',
    lastUpdatedAt: new Date().toISOString(),
    lastUpdatedBy: actorLabel,
  }
}

function normalizeFieldValue(fieldKey: string, nextValue: string) {
  if (fieldKey === 'entry_number') {
    return nextValue.toUpperCase()
  }
  return nextValue
}

function sectionIdForField(documentType: SupportedDocumentType, fieldKey: string) {
  return DOCUMENT_DEFINITIONS[documentType].sections.find(
    (section) => section.kind !== 'line_items' && section.fields.some((field) => field.key === fieldKey),
  )?.id
}

function lineItemSectionIdForDocumentType(documentType: SupportedDocumentType) {
  return DOCUMENT_DEFINITIONS[documentType].sections.find((section) => section.kind === 'line_items')?.id
}

function reviewGroupIdForField(fieldKey: string) {
  if (fieldKey.startsWith('ultimate_consignee_')) return 'block_29_review'
  if (fieldKey.startsWith('importer_of_record_')) return 'block_30_review'
  return undefined
}

function applyStructuredRecordAutomation(record: StructuredDocumentRecord, actorLabel: string) {
  if (record.documentType === 'cbp_7501') {
    return applyCbp7501DocumentAutomation(record, actorLabel)
  }
  return record
}

export function DocumentsWorkspaceTab({
  caseId,
  actorId,
  actorLabel,
  sourceDocuments,
  structuredDocuments,
  visibleDocumentTypes = MVP_VISIBLE_DOCUMENT_TYPES,
  showUploadArea = true,
  focusTarget,
  onUploaded,
  onSourceDocumentsChange,
  onStructuredDocumentsChange,
  onActivityEvent,
  onFocusHandled,
}: DocumentsWorkspaceTabProps) {
  const visibleStructuredDocuments = useMemo(
    () =>
      filterVisibleStructuredDocuments(structuredDocuments).filter((record) =>
        visibleDocumentTypes.includes(record.documentType),
      ),
    [structuredDocuments, visibleDocumentTypes],
  )
  const visibleSourceDocuments = useMemo(
    () => filterVisibleSourceDocuments(sourceDocuments, visibleStructuredDocuments),
    [sourceDocuments, visibleStructuredDocuments],
  )
  const [selected, setSelected] = useState<NavigatorTarget>(() => {
    if (visibleStructuredDocuments[0]) return { kind: 'structured', id: visibleStructuredDocuments[0].id }
    if (visibleSourceDocuments[0]) return { kind: 'source', id: visibleSourceDocuments[0].id }
    return { kind: 'structured', id: '' }
  })
  const [busyRecordId, setBusyRecordId] = useState<string | null>(null)
  const [saveMessage, setSaveMessage] = useState<string | null>(null)
  const [selectedSourceIds, setSelectedSourceIds] = useState<string[]>([])
  const [removalBusy, setRemovalBusy] = useState(false)
  const [removalMessage, setRemovalMessage] = useState<string | null>(null)
  const [removalError, setRemovalError] = useState<string | null>(null)
  const structuredDocumentsRef = useRef(structuredDocuments)
  const pendingAutoSaveIdsRef = useRef<Set<string>>(new Set())

  useEffect(() => {
    structuredDocumentsRef.current = structuredDocuments
  }, [structuredDocuments])

  const effectiveSelected = useMemo<NavigatorTarget>(() => {
    if (focusTarget) {
      return { kind: 'structured', id: focusTarget.documentId }
    }
    if (selected.kind === 'structured' && visibleStructuredDocuments.some((record) => record.id === selected.id)) {
      return selected
    }
    if (selected.kind === 'source' && visibleSourceDocuments.some((record) => record.id === selected.id)) {
      return selected
    }
    if (visibleStructuredDocuments[0]) {
      return { kind: 'structured', id: visibleStructuredDocuments[0].id }
    }
    if (visibleSourceDocuments[0]) {
      return { kind: 'source', id: visibleSourceDocuments[0].id }
    }
    return { kind: 'structured', id: '' }
  }, [focusTarget, selected, visibleSourceDocuments, visibleStructuredDocuments])

  const selectedStructuredRecord = visibleStructuredDocuments.find(
    (record) => effectiveSelected.kind === 'structured' && record.id === effectiveSelected.id,
  )
  const selectedSourceRecord = visibleSourceDocuments.find(
    (record) => effectiveSelected.kind === 'source' && record.id === effectiveSelected.id,
  )

  const requiredDocsSummary = useMemo(() => {
    const complete = visibleStructuredDocuments.filter((record) => {
      const definition = DOCUMENT_DEFINITIONS[record.documentType]
      return definition.requiredForReview && record.status === 'complete'
    }).length
    const required = visibleStructuredDocuments.filter(
      (record) => DOCUMENT_DEFINITIONS[record.documentType].requiredForReview,
    ).length

    return { complete, required }
  }, [visibleStructuredDocuments])
  const selectedStructuredSummaryTiles = useMemo(() => {
    if (!selectedStructuredRecord) return []
    const validationSummary = summarizeValidationIssues(selectedStructuredRecord.validationIssues)
    return [
      {
        label: 'Flags',
        value: `${validationSummary.errors + validationSummary.warnings}`,
        icon: <AlertTriangle size={18} aria-hidden="true" />,
      },
      {
        label: 'Line items',
        value: `${selectedStructuredRecord.lineItems.length}`,
        icon: <ListOrdered size={18} aria-hidden="true" />,
      },
      {
        label: 'Last saved',
        value: formatDateTime(selectedStructuredRecord.lastUpdatedAt),
        icon: <Clock3 size={18} aria-hidden="true" />,
      },
    ]
  }, [selectedStructuredRecord])
  const showRequiredDocumentsPanel = false

  function handleSelect(next: NavigatorTarget) {
    if (focusTarget) {
      onFocusHandled()
    }
    setSelected(next)
  }

  async function saveRecord(
    record: StructuredDocumentRecord,
    options?: { auto?: boolean },
  ) {
    if (busyRecordId === record.id) {
      pendingAutoSaveIdsRef.current.add(record.id)
      return
    }

    setBusyRecordId(record.id)
    setSaveMessage(null)

    try {
      const { record: savedRecord, event } = await caseWorkspaceService.saveStructuredDocument({
        record,
        actorId,
        actorLabel,
      })
      onActivityEvent(event)
      onStructuredDocumentsChange(
        structuredDocumentsRef.current.map((current) => (current.id === savedRecord.id ? savedRecord : current)),
      )
      setSaveMessage(
        options?.auto
          ? `${savedRecord.title} saved automatically.`
          : `${savedRecord.title} saved to the case workspace.`,
      )
    } finally {
      setBusyRecordId((current) => (current === record.id ? null : current))
      if (pendingAutoSaveIdsRef.current.has(record.id)) {
        pendingAutoSaveIdsRef.current.delete(record.id)
        window.setTimeout(() => {
          void saveRecordById(record.id, { auto: true })
        }, 0)
      }
    }
  }

  function saveRecordById(recordId: string, options?: { auto?: boolean }) {
    const latestRecord = structuredDocumentsRef.current.find((record) => record.id === recordId)
    if (!latestRecord) return Promise.resolve()
    return saveRecord(latestRecord, options)
  }

  function updateStructuredRecord(
    recordId: string,
    updater: (record: StructuredDocumentRecord) => StructuredDocumentRecord,
  ) {
    const nextRecords = structuredDocumentsRef.current.map((record) => (record.id === recordId ? updater(record) : record))
    structuredDocumentsRef.current = nextRecords
    onStructuredDocumentsChange(nextRecords)
  }

  function resetSectionCompletionOnEdit(
    record: StructuredDocumentRecord,
    sectionIds: Array<string | undefined>,
    detail: string,
  ) {
    const validSectionIds = sectionIds.filter((sectionId): sectionId is string => Boolean(sectionId))
    if (validSectionIds.length === 0) return record
    const sectionIdsToReset = validSectionIds.filter((sectionId) => record.sectionStates[sectionId]?.status === 'complete')
    const shouldResetDocument = record.status === 'complete'

    if (sectionIdsToReset.length === 0 && !shouldResetDocument) return record

    return {
      ...record,
      status: shouldResetDocument ? 'in_progress' : record.status,
      sectionStates:
        sectionIdsToReset.length > 0
          ? {
              ...record.sectionStates,
              ...Object.fromEntries(
                sectionIdsToReset.map((sectionId) => [
                  sectionId,
                  {
                    ...record.sectionStates[sectionId],
                    sectionId,
                    status: 'in_progress',
                    lastUpdatedAt: new Date().toISOString(),
                    lastUpdatedBy: actorLabel,
                  },
                ]),
              ),
            }
          : record.sectionStates,
      auditHistory: [
        {
          id: `${record.id}-${sectionIdsToReset.join('-') || 'document'}-section-reopened-${Date.now()}`,
          at: new Date().toISOString(),
          actor: actorLabel,
          action: 'section_reopened_for_editing',
          detail,
        },
        ...record.auditHistory,
      ],
      lastUpdatedAt: new Date().toISOString(),
      lastUpdatedBy: actorLabel,
    }
  }

  function toggleSourceSelection(sourceId: string, checked: boolean) {
    setSelectedSourceIds((current) =>
      checked ? [...new Set([...current, sourceId])] : current.filter((id) => id !== sourceId),
    )
  }

  async function removeSourceDocumentsByIds(sourceIds: string[]) {
    const sourceRecordsToRemove = sourceDocuments.filter((record) => sourceIds.includes(record.id))
    if (sourceRecordsToRemove.length === 0) return

    const removalDescription =
      sourceRecordsToRemove.length === 1
        ? `Remove ${sourceRecordsToRemove[0].title} from this case? This removes the uploaded file only. Structured document records will stay in place.`
        : `Remove ${sourceRecordsToRemove.length} uploaded files from this case? This removes the uploaded files only. Structured document records will stay in place.`

    if (!window.confirm(removalDescription)) return

    setRemovalBusy(true)
    setRemovalError(null)
    setRemovalMessage(null)

    try {
      const selectedLinkedStructuredRecord =
        selectedSourceRecord?.linkedStructuredDocumentId
          ? structuredDocuments.find((record) => record.id === selectedSourceRecord.linkedStructuredDocumentId)
          : undefined

      const { removedIds, structuredDocuments: nextStructuredDocuments, event } =
        await caseWorkspaceService.removeSourceDocuments({
          caseId,
          documents: sourceRecordsToRemove,
          structuredDocuments,
          actorId,
          actorLabel,
        })

      const nextSourceDocuments = sourceDocuments.filter((record) => !removedIds.includes(record.id))
      onSourceDocumentsChange(nextSourceDocuments, nextStructuredDocuments, removedIds)
      if (event) {
        onActivityEvent(event)
      }

      setSelectedSourceIds((current) => current.filter((id) => !removedIds.includes(id)))

      if (effectiveSelected.kind === 'source' && removedIds.includes(effectiveSelected.id)) {
        if (selectedLinkedStructuredRecord) {
          setSelected({ kind: 'structured', id: selectedLinkedStructuredRecord.id })
        } else if (nextStructuredDocuments[0]) {
          setSelected({ kind: 'structured', id: nextStructuredDocuments[0].id })
        } else if (nextSourceDocuments[0]) {
          setSelected({ kind: 'source', id: nextSourceDocuments[0].id })
        } else {
          setSelected({ kind: 'structured', id: '' })
        }
      }

      setRemovalMessage(
        sourceRecordsToRemove.length === 1
          ? `${sourceRecordsToRemove[0].title} was removed from the case.`
          : `${sourceRecordsToRemove.length} uploaded files were removed from the case.`,
      )
    } catch (error) {
      const message = error instanceof Error ? error.message : 'The uploaded files could not be removed.'
      setRemovalError(message)
    } finally {
      setRemovalBusy(false)
    }
  }

  function updateField(recordId: string, fieldKey: string, nextValue: string) {
    updateStructuredRecord(recordId, (record) => {
      const normalizedValue = normalizeFieldValue(fieldKey, nextValue)
      const sectionId = sectionIdForField(record.documentType, fieldKey)
      const reviewGroupId = reviewGroupIdForField(fieldKey)
      const existingField = record.fields[fieldKey]
      const nextField: DocumentFieldState = {
        ...existingField,
        value: normalizedValue,
        displayValue: normalizedValue,
        origin: 'manual',
        reviewStatus:
          existingField?.origin === 'ai_extracted' && existingField.value !== normalizedValue
            ? 'edited_after_extraction'
            : 'manually_entered',
        reviewedValue: normalizedValue,
        overrideEnabled: existingField?.overrideEnabled,
        overrideReason: existingField?.overrideReason,
        lastUpdatedAt: new Date().toISOString(),
        lastUpdatedBy: actorLabel,
      }

      const nextRecord = {
        ...record,
        status: 'in_progress',
        fields: {
          ...record.fields,
          [fieldKey]: nextField,
        },
        awaRecommendations: record.awaRecommendations.map((recommendation) =>
          recommendation.fieldKey === fieldKey
            ? {
                ...recommendation,
                currentValue: normalizedValue,
                state:
                  recommendation.recommendedValue && recommendation.recommendedValue === normalizedValue
                    ? 'resolved'
                    : recommendation.currentValue !== normalizedValue
                      ? 'partially_applied'
                      : recommendation.state,
              }
            : recommendation,
        ),
        auditHistory: [
          {
            id: `${record.id}-${fieldKey}-${Date.now()}`,
            at: new Date().toISOString(),
            actor: actorLabel,
            action: 'field_updated',
            detail: `${fieldKey} updated in ${record.title}.`,
          },
          ...record.auditHistory,
        ],
        lastUpdatedAt: new Date().toISOString(),
        lastUpdatedBy: actorLabel,
      }

      return applyStructuredRecordAutomation(
        resetSectionCompletionOnEdit(
          nextRecord,
          [sectionId, reviewGroupId],
          `${fieldKey} was edited after the section had been marked complete.`,
        ),
        actorLabel,
      )
    })
  }

  function updateFieldNote(recordId: string, fieldKey: string, reviewNote: string) {
    updateStructuredRecord(recordId, (record) => ({
      ...record,
      fields: {
        ...record.fields,
        [fieldKey]: {
          ...record.fields[fieldKey],
          reviewNote,
          lastUpdatedAt: new Date().toISOString(),
          lastUpdatedBy: actorLabel,
        },
      },
      lastUpdatedAt: new Date().toISOString(),
      lastUpdatedBy: actorLabel,
    }))
  }

  function confirmField(recordId: string, fieldKey: string) {
    updateStructuredRecord(recordId, (record) => ({
      ...record,
      fields: {
        ...record.fields,
        [fieldKey]: {
          ...record.fields[fieldKey],
          reviewStatus: 'agent_confirmed',
          agentConfirmedAt: new Date().toISOString(),
          agentConfirmedBy: actorLabel,
          lastUpdatedAt: new Date().toISOString(),
          lastUpdatedBy: actorLabel,
        },
      },
      lastUpdatedAt: new Date().toISOString(),
      lastUpdatedBy: actorLabel,
    }))
  }

  function setFieldOverride(recordId: string, fieldKey: string, enabled: boolean, reason: string) {
    updateStructuredRecord(recordId, (record) => ({
      ...record,
      fields: {
        ...record.fields,
        [fieldKey]: {
          ...record.fields[fieldKey],
          overrideEnabled: enabled || undefined,
          overrideReason: enabled ? reason : undefined,
          lastUpdatedAt: new Date().toISOString(),
          lastUpdatedBy: actorLabel,
        },
      },
      auditHistory: [
        {
          id: `${record.id}-${fieldKey}-override-${Date.now()}`,
          at: new Date().toISOString(),
          actor: actorLabel,
          action: enabled ? 'field_override_enabled' : 'field_override_cleared',
          detail: enabled
            ? `${fieldKey} override enabled for CBP review: ${reason}`
            : `${fieldKey} override cleared.`,
        },
        ...record.auditHistory,
      ],
      lastUpdatedAt: new Date().toISOString(),
      lastUpdatedBy: actorLabel,
    }))
  }

  function updateSectionNote(recordId: string, sectionId: string, note: string) {
    updateStructuredRecord(recordId, (record) => ({
      ...record,
      sectionStates: {
        ...record.sectionStates,
        [sectionId]: {
          sectionId,
          status: summarizeSectionStatus(record, sectionId),
          note,
          lastUpdatedAt: new Date().toISOString(),
          lastUpdatedBy: actorLabel,
        },
      },
      lastUpdatedAt: new Date().toISOString(),
      lastUpdatedBy: actorLabel,
    }))
  }

  function markSectionComplete(recordId: string, sectionId: string) {
    updateStructuredRecord(recordId, (record) => {
      const currentSectionState = record.sectionStates[sectionId]
      const isCurrentlyComplete = currentSectionState?.status === 'complete'

      return {
        ...record,
        status: record.status === 'complete' ? 'in_progress' : record.status,
        sectionStates: {
          ...record.sectionStates,
          [sectionId]: {
            sectionId,
            status: isCurrentlyComplete ? 'in_progress' : 'complete',
            note: currentSectionState?.note,
            lastUpdatedAt: new Date().toISOString(),
            lastUpdatedBy: actorLabel,
          },
        },
        auditHistory: [
          {
            id: `${record.id}-${sectionId}-section-complete-${Date.now()}`,
            at: new Date().toISOString(),
            actor: actorLabel,
            action: isCurrentlyComplete ? 'section_marked_in_progress' : 'section_marked_complete',
            detail: isCurrentlyComplete
              ? `${sectionId} moved back to in progress in ${record.title}.`
              : `${sectionId} marked complete in ${record.title}.`,
          },
          ...record.auditHistory,
        ],
        lastUpdatedAt: new Date().toISOString(),
        lastUpdatedBy: actorLabel,
      }
    })
  }

  function confirmFieldGroup(recordId: string, fieldKeys: string[]) {
    updateStructuredRecord(recordId, (record) => ({
      ...record,
      fields: Object.fromEntries(
        Object.entries(record.fields).map(([fieldKey, fieldState]) => [
          fieldKey,
          fieldKeys.includes(fieldKey)
            ? {
                ...fieldState,
                reviewStatus: 'agent_confirmed',
                agentConfirmedAt: new Date().toISOString(),
                agentConfirmedBy: actorLabel,
                lastUpdatedAt: new Date().toISOString(),
                lastUpdatedBy: actorLabel,
              }
            : fieldState,
        ]),
      ),
      lastUpdatedAt: new Date().toISOString(),
      lastUpdatedBy: actorLabel,
    }))
  }

  function updateLineItemField(recordId: string, lineItemId: string, fieldKey: string, nextValue: string) {
    updateStructuredRecord(recordId, (record) => {
      const sectionId = lineItemSectionIdForDocumentType(record.documentType)
      const nextRecord = {
        ...record,
        status: 'in_progress',
        lineItems: record.lineItems.map((lineItem) =>
          lineItem.id !== lineItemId
            ? lineItem
            : (() => {
                const updatedLineItem = {
                  ...lineItem,
                  status: 'in_progress',
                  fields: {
                    ...lineItem.fields,
                    [fieldKey]: {
                      ...lineItem.fields[fieldKey],
                      value: nextValue,
                      displayValue: nextValue,
                      origin: 'manual',
                      reviewStatus:
                        lineItem.fields[fieldKey]?.origin === 'ai_extracted'
                          ? 'edited_after_extraction'
                          : 'manually_entered',
                      reviewedValue: nextValue,
                      lastUpdatedAt: new Date().toISOString(),
                      lastUpdatedBy: actorLabel,
                    },
                  },
                  auditHistory: [
                    {
                      id: `${lineItem.id}-${fieldKey}-${Date.now()}`,
                      at: new Date().toISOString(),
                      actor: actorLabel,
                      action: 'line_item_field_updated',
                      detail: `${fieldKey} updated on line ${lineItem.lineNumber}.`,
                    },
                    ...(lineItem.auditHistory ?? []),
                  ],
                }

                return record.documentType === 'cbp_7501'
                  ? applyCbp7501LineAutomation(updatedLineItem, actorLabel)
                  : updatedLineItem
              })(),
        ),
        lastUpdatedAt: new Date().toISOString(),
        lastUpdatedBy: actorLabel,
      }

      return applyStructuredRecordAutomation(
        resetSectionCompletionOnEdit(
          nextRecord,
          [sectionId],
          `${fieldKey} was edited in the line item section after it had been marked complete.`,
        ),
        actorLabel,
      )
    })
  }

  function confirmLineItemField(recordId: string, lineItemId: string, fieldKey: string) {
    updateStructuredRecord(recordId, (record) => ({
      ...record,
      lineItems: record.lineItems.map((lineItem) =>
        lineItem.id !== lineItemId
          ? lineItem
          : {
              ...lineItem,
              fields: {
                ...lineItem.fields,
                [fieldKey]: {
                  ...lineItem.fields[fieldKey],
                  reviewStatus: 'agent_confirmed',
                  agentConfirmedAt: new Date().toISOString(),
                  agentConfirmedBy: actorLabel,
                  lastUpdatedAt: new Date().toISOString(),
                  lastUpdatedBy: actorLabel,
                },
              },
            },
      ),
      lastUpdatedAt: new Date().toISOString(),
      lastUpdatedBy: actorLabel,
    }))
  }

  function confirmLineItemGroup(recordId: string, lineItemId: string, fieldKeys: string[]) {
    updateStructuredRecord(recordId, (record) => ({
      ...record,
      lineItems: record.lineItems.map((lineItem) =>
        lineItem.id !== lineItemId
          ? lineItem
          : {
              ...lineItem,
              fields: Object.fromEntries(
                Object.entries(lineItem.fields).map(([fieldKey, fieldState]) => [
                  fieldKey,
                  fieldKeys.includes(fieldKey)
                    ? {
                        ...fieldState,
                        reviewStatus: 'agent_confirmed',
                        agentConfirmedAt: new Date().toISOString(),
                        agentConfirmedBy: actorLabel,
                        lastUpdatedAt: new Date().toISOString(),
                        lastUpdatedBy: actorLabel,
                      }
                    : fieldState,
                ]),
              ),
              auditHistory: [
                {
                  id: `${lineItem.id}-group-confirm-${Date.now()}`,
                  at: new Date().toISOString(),
                  actor: actorLabel,
                  action: 'line_item_group_confirmed',
                  detail: `Line ${lineItem.lineNumber} was agent confirmed.`,
                },
                ...(lineItem.auditHistory ?? []),
              ],
            },
      ),
      lastUpdatedAt: new Date().toISOString(),
      lastUpdatedBy: actorLabel,
    }))
  }

  function setLineItemFieldOverride(
    recordId: string,
    lineItemId: string,
    fieldKey: string,
    enabled: boolean,
    reason: string,
  ) {
    updateStructuredRecord(recordId, (record) => ({
      ...record,
      lineItems: record.lineItems.map((lineItem) =>
        lineItem.id !== lineItemId
          ? lineItem
          : {
              ...lineItem,
              fields: {
                ...lineItem.fields,
                [fieldKey]: {
                  ...lineItem.fields[fieldKey],
                  overrideEnabled: enabled || undefined,
                  overrideReason: enabled ? reason : undefined,
                  lastUpdatedAt: new Date().toISOString(),
                  lastUpdatedBy: actorLabel,
                },
              },
              auditHistory: [
                {
                  id: `${lineItem.id}-${fieldKey}-override-${Date.now()}`,
                  at: new Date().toISOString(),
                  actor: actorLabel,
                  action: enabled ? 'line_item_field_override_enabled' : 'line_item_field_override_cleared',
                  detail: enabled
                    ? `${fieldKey} override enabled on line ${lineItem.lineNumber}: ${reason}`
                    : `${fieldKey} override cleared on line ${lineItem.lineNumber}.`,
                },
                ...(lineItem.auditHistory ?? []),
              ],
            },
      ),
      lastUpdatedAt: new Date().toISOString(),
      lastUpdatedBy: actorLabel,
    }))
  }

  function updateLineItemNote(recordId: string, lineItemId: string, note: string) {
    updateStructuredRecord(recordId, (record) => ({
      ...record,
      lineItems: record.lineItems.map((lineItem) =>
        lineItem.id !== lineItemId
          ? lineItem
          : {
              ...lineItem,
              note,
              auditHistory: [
                {
                  id: `${lineItem.id}-note-${Date.now()}`,
                  at: new Date().toISOString(),
                  actor: actorLabel,
                  action: 'line_item_note_updated',
                  detail: `Review note updated for line ${lineItem.lineNumber}.`,
                },
                ...(lineItem.auditHistory ?? []),
              ],
            },
      ),
      lastUpdatedAt: new Date().toISOString(),
      lastUpdatedBy: actorLabel,
    }))
  }

  function lineItemDefinitionsForRecord(record: StructuredDocumentRecord): DocumentFieldDefinition[] {
    if (record.documentType === 'cbp_7501') return CBP_7501_LINE_ITEM_FIELDS
    if (record.documentType === 'manifest') return MANIFEST_1302_SHIPMENT_FIELDS
    if (record.documentType === 'commercial_invoice') return COMMERCIAL_INVOICE_LINE_ITEM_FIELDS
    return []
  }

  function reindexLineItems(lineItems: StructuredDocumentRecord['lineItems']) {
    return lineItems.map((lineItem, index) => ({
      ...lineItem,
      lineNumber: String(index + 1).padStart(3, '0'),
      fields: {
        ...lineItem.fields,
        ...(lineItem.fields.line_number
          ? {
              line_number: {
                ...lineItem.fields.line_number,
                value: String(index + 1).padStart(3, '0'),
                displayValue: String(index + 1).padStart(3, '0'),
                lastUpdatedAt: new Date().toISOString(),
                lastUpdatedBy: actorLabel,
              },
            }
          : {}),
      },
    }))
  }

  function addLineItem(recordId: string) {
    updateStructuredRecord(recordId, (record) => {
      const sectionId = lineItemSectionIdForDocumentType(record.documentType)
      const lastLine = record.lineItems[record.lineItems.length - 1]
      const nextLineNumber = String(record.lineItems.length + 1).padStart(3, '0')
      const lineItemDefinitions = lineItemDefinitionsForRecord(record)
      const baseFields = lastLine?.fields ?? Object.fromEntries(
        lineItemDefinitions.map((definition) => [
          definition.key,
          {
            value: '',
            displayValue: '',
            origin: definition.key === 'line_number' ? 'system' : 'manual',
            reviewStatus: definition.key === 'line_number' ? undefined : 'manually_entered',
            lastUpdatedAt: new Date().toISOString(),
            lastUpdatedBy: actorLabel,
          } satisfies DocumentFieldState,
        ]),
      )
      const newLineItem = {
        id: crypto.randomUUID(),
        lineNumber: nextLineNumber,
        status: 'not_started' as const,
        note: '',
        fields: Object.fromEntries(
          Object.entries(baseFields).map(([fieldKey, fieldState]) => [
            fieldKey,
            {
              ...fieldState,
              value: fieldKey === 'line_number' ? nextLineNumber : '',
              displayValue: fieldKey === 'line_number' ? nextLineNumber : '',
              origin: fieldKey === 'line_number' ? 'system' : 'manual',
              reviewStatus: fieldKey === 'line_number' ? undefined : 'manually_entered',
              sourceValue: undefined,
              sourceConfidence: undefined,
              reviewedValue: undefined,
              sourceDocumentId: undefined,
              sourceDocumentTitle: undefined,
              extractedFieldKey: undefined,
              conflictNote: undefined,
              reviewNote: undefined,
              lastUpdatedAt: new Date().toISOString(),
              lastUpdatedBy: actorLabel,
            },
          ]),
        ),
        auditHistory: [
          {
            id: crypto.randomUUID(),
            at: new Date().toISOString(),
            actor: actorLabel,
            action: 'line_item_created',
            detail: `Line ${nextLineNumber} added to ${record.title}.`,
          },
        ],
      }

      const normalizedLineItem = record.documentType === 'cbp_7501'
        ? applyCbp7501LineAutomation(newLineItem, actorLabel)
        : newLineItem

      const nextRecord = {
        ...record,
        lineItems: [...record.lineItems, normalizedLineItem],
        lastUpdatedAt: new Date().toISOString(),
        lastUpdatedBy: actorLabel,
      }
      return applyStructuredRecordAutomation(
        resetSectionCompletionOnEdit(
          nextRecord,
          [sectionId],
          `A new line item was added after the line item section had been marked complete.`,
        ),
        actorLabel,
      )
    })
  }

  function duplicateLineItem(recordId: string, lineItemId: string) {
    updateStructuredRecord(recordId, (record) => {
      const sectionId = lineItemSectionIdForDocumentType(record.documentType)
      const lineItem = record.lineItems.find((item) => item.id === lineItemId)
      if (!lineItem) return record
      const duplicated = {
        ...lineItem,
        id: crypto.randomUUID(),
        auditHistory: [
          {
            id: crypto.randomUUID(),
            at: new Date().toISOString(),
            actor: actorLabel,
            action: 'line_item_duplicated',
            detail: `Line ${lineItem.lineNumber} duplicated.`,
          },
          ...(lineItem.auditHistory ?? []),
        ],
      }
      const normalizedDuplicate = record.documentType === 'cbp_7501'
        ? applyCbp7501LineAutomation(duplicated, actorLabel)
        : duplicated
      const insertionIndex = record.lineItems.findIndex((item) => item.id === lineItemId) + 1
      const reordered = [...record.lineItems]
      reordered.splice(insertionIndex, 0, normalizedDuplicate)
      const nextRecord = {
        ...record,
        lineItems: reindexLineItems(reordered),
        lastUpdatedAt: new Date().toISOString(),
        lastUpdatedBy: actorLabel,
      }
      return applyStructuredRecordAutomation(
        resetSectionCompletionOnEdit(
          nextRecord,
          [sectionId],
          `A line item was duplicated after the line item section had been marked complete.`,
        ),
        actorLabel,
      )
    })
  }

  function removeLineItem(recordId: string, lineItemId: string) {
    updateStructuredRecord(recordId, (record) => {
      const sectionId = lineItemSectionIdForDocumentType(record.documentType)
      const nextRecord = {
        ...record,
        lineItems: reindexLineItems(record.lineItems.filter((lineItem) => lineItem.id !== lineItemId)),
        lastUpdatedAt: new Date().toISOString(),
        lastUpdatedBy: actorLabel,
      }
      return applyStructuredRecordAutomation(
        resetSectionCompletionOnEdit(
          nextRecord,
          [sectionId],
          `A line item was removed after the line item section had been marked complete.`,
        ),
        actorLabel,
      )
    })
  }

  function moveLineItem(recordId: string, lineItemId: string, direction: 'up' | 'down') {
    updateStructuredRecord(recordId, (record) => {
      const sectionId = lineItemSectionIdForDocumentType(record.documentType)
      const currentIndex = record.lineItems.findIndex((lineItem) => lineItem.id === lineItemId)
      if (currentIndex < 0) return record
      const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1
      if (targetIndex < 0 || targetIndex >= record.lineItems.length) return record
      const nextLineItems = [...record.lineItems]
      const [lineItem] = nextLineItems.splice(currentIndex, 1)
      nextLineItems.splice(targetIndex, 0, lineItem)

      const nextRecord = {
        ...record,
        lineItems: reindexLineItems(nextLineItems),
        lastUpdatedAt: new Date().toISOString(),
        lastUpdatedBy: actorLabel,
      }
      return applyStructuredRecordAutomation(
        resetSectionCompletionOnEdit(
          nextRecord,
          [sectionId],
          `A line item was reordered after the line item section had been marked complete.`,
        ),
        actorLabel,
      )
    })
  }

  function applyRecommendation(recordId: string, recommendationId: string) {
    updateStructuredRecord(recordId, (record) => {
      const recommendation = record.awaRecommendations.find((item) => item.id === recommendationId)
      if (!recommendation || !recommendation.recommendedValue) return record
      const sectionId = sectionIdForField(record.documentType, recommendation.fieldKey)
      const nextRecord = {
        ...record,
        fields: {
          ...record.fields,
          [recommendation.fieldKey]: {
            ...record.fields[recommendation.fieldKey],
            value: recommendation.recommendedValue,
            displayValue: recommendation.recommendedValue,
            origin: 'reviewed',
            reviewedValue: recommendation.recommendedValue,
            lastUpdatedAt: new Date().toISOString(),
            lastUpdatedBy: actorLabel,
          },
        },
        awaRecommendations: record.awaRecommendations.map((item) =>
          item.id === recommendationId
            ? { ...item, currentValue: recommendation.recommendedValue ?? item.currentValue, state: 'resolved' }
            : item,
        ),
        auditHistory: [
          {
            id: `${record.id}-${recommendationId}-applied`,
            at: new Date().toISOString(),
            actor: actorLabel,
            action: 'recommendation_applied',
            detail: `AWA recommendation applied to ${recommendation.fieldKey}.`,
          },
          ...record.auditHistory,
        ],
        lastUpdatedAt: new Date().toISOString(),
        lastUpdatedBy: actorLabel,
      }
      return applyStructuredRecordAutomation(
        resetSectionCompletionOnEdit(
          nextRecord,
          [sectionId],
          `${recommendation.fieldKey} was updated from an AWA recommendation after the section had been marked complete.`,
        ),
        actorLabel,
      )
    })
  }

  function markDocumentComplete(recordId: string) {
    updateStructuredRecord(recordId, (record) => ({
      ...record,
      status: record.status === 'complete' ? 'in_progress' : 'complete',
      auditHistory: [
        {
          id: `${record.id}-document-complete-${Date.now()}`,
          at: new Date().toISOString(),
          actor: actorLabel,
          action: record.status === 'complete' ? 'document_marked_in_progress' : 'document_marked_complete',
          detail:
            record.status === 'complete'
              ? `${record.title} moved back to in progress.`
              : `${record.title} marked complete.`,
        },
        ...record.auditHistory,
      ],
      lastUpdatedAt: new Date().toISOString(),
      lastUpdatedBy: actorLabel,
    }))

    void saveRecordById(recordId)
  }

  function createRecord(documentType: SupportedDocumentType) {
    const baseRecord = structuredDocuments.find((record) => record.documentType === documentType)
    const nextVersion = structuredDocuments.filter((record) => record.documentType === documentType).length + 1
    const newId = crypto.randomUUID()
    const now = new Date().toISOString()
    const definition = DOCUMENT_DEFINITIONS[documentType]
    const duplicatedRecord: StructuredDocumentRecord = baseRecord
      ? {
          ...baseRecord,
          id: newId,
          title: nextVersion === 1 ? baseRecord.title : `${baseRecord.title} ${nextVersion}`,
          version: nextVersion,
          status: 'draft',
          createdAt: now,
          createdBy: actorLabel,
          lastUpdatedAt: now,
          lastUpdatedBy: actorLabel,
          sourceLinks: [...baseRecord.sourceLinks],
          fields: Object.fromEntries(
            Object.entries(baseRecord.fields).map(([fieldKey, fieldState]) => [fieldKey, { ...fieldState }]),
          ),
          lineItems: baseRecord.lineItems.map((lineItem) => ({
            ...lineItem,
            id: crypto.randomUUID(),
            fields: Object.fromEntries(
              Object.entries(lineItem.fields).map(([fieldKey, fieldState]) => [fieldKey, { ...fieldState }]),
            ),
            auditHistory: [...(lineItem.auditHistory ?? [])],
          })),
          sectionStates: Object.fromEntries(
            Object.entries(baseRecord.sectionStates).map(([sectionId, sectionState]) => [sectionId, { ...sectionState }]),
          ),
          awaRecommendations: baseRecord.awaRecommendations.map((recommendation) => ({
            ...recommendation,
            id: crypto.randomUUID(),
            documentId: newId,
          })),
          validationIssues: [],
          auditHistory: [
            {
              id: crypto.randomUUID(),
              at: now,
              actor: actorLabel,
              action: 'created',
              detail: `Structured ${baseRecord.title} record created from template.`,
            },
            ...baseRecord.auditHistory,
          ],
        }
      : {
          id: newId,
          caseId,
          kind: 'structured',
          documentType,
          title: definition.label,
          status: 'draft',
          version: nextVersion,
          schemaVersion: definition.schemaVersion,
          createdAt: now,
          createdBy: actorLabel,
          lastUpdatedAt: now,
          lastUpdatedBy: actorLabel,
          sourceLinks: [],
          fields: Object.fromEntries(
            definition.sections.flatMap((section) =>
              section.fields.map((field) => [field.key, buildEmptyFieldState(actorLabel)]),
            ),
          ),
          lineItems: [],
          sectionStates: Object.fromEntries(
            definition.sections.map((section) => [
              section.id,
              {
                sectionId: section.id,
                status: 'not_started',
                lastUpdatedAt: now,
                lastUpdatedBy: actorLabel,
              },
            ]),
          ),
          awaRecommendations: [],
          validationIssues: [],
          auditHistory: [
            {
              id: crypto.randomUUID(),
              at: now,
              actor: actorLabel,
              action: 'created',
              detail: `Structured ${definition.label} record created from template.`,
            },
          ],
        }

    const automatedRecord = applyStructuredRecordAutomation(duplicatedRecord, actorLabel)
    onStructuredDocumentsChange([...structuredDocuments, automatedRecord])
    setSelected({ kind: 'structured', id: automatedRecord.id })
  }

  return (
    <div className="space-y-4">
      <ReadinessOverviewCard
        title="Document readiness"
        description=""
        complete={requiredDocsSummary.complete}
        total={requiredDocsSummary.required}
        summaryTiles={selectedStructuredSummaryTiles}
      />

      <div className={showRequiredDocumentsPanel ? 'grid gap-4 xl:grid-cols-[18rem_minmax(0,1fr)]' : 'space-y-3'}>
        {showRequiredDocumentsPanel && (
          <aside className="space-y-3">
            <RequiredDocumentsPanel
              structuredDocuments={visibleStructuredDocuments}
              sourceDocuments={visibleSourceDocuments}
              visibleDocumentTypes={visibleDocumentTypes}
              selected={effectiveSelected}
              selectedSourceIds={selectedSourceIds}
              removalBusy={removalBusy}
              removalMessage={removalMessage}
              removalError={removalError}
              onOpenRecord={(recordId) => handleSelect({ kind: 'structured', id: recordId })}
              onOpenSource={(sourceId) => handleSelect({ kind: 'source', id: sourceId })}
              onToggleSourceSelection={toggleSourceSelection}
              onRemoveSource={(sourceId) => void removeSourceDocumentsByIds([sourceId])}
              onRemoveSelected={() => void removeSourceDocumentsByIds(selectedSourceIds)}
              onCreateRecord={createRecord}
            />
          </aside>
        )}
        <main className="space-y-3">
          {showUploadArea && (
            <WorkspaceUploadPanel
              caseId={caseId}
              onUploaded={onUploaded}
              selectedStructuredRecord={selectedStructuredRecord}
              selectedSourceRecord={selectedSourceRecord}
              linkedStructuredRecord={
                selectedSourceRecord?.linkedStructuredDocumentId
                  ? visibleStructuredDocuments.find((record) => record.id === selectedSourceRecord.linkedStructuredDocumentId)
                  : undefined
              }
            />
          )}
          {selectedStructuredRecord ? (
            <StructuredDocumentEditor
              record={selectedStructuredRecord}
              focusTarget={focusTarget}
              busy={busyRecordId === selectedStructuredRecord.id}
              saveMessage={saveMessage}
              onFieldChange={updateField}
              onFieldNoteChange={updateFieldNote}
              onConfirmField={confirmField}
              onSetFieldOverride={setFieldOverride}
              onSectionNoteChange={updateSectionNote}
              onMarkSectionComplete={markSectionComplete}
              onLineItemFieldChange={updateLineItemField}
              onLineItemNoteChange={updateLineItemNote}
              onConfirmLineItemField={confirmLineItemField}
              onConfirmLineItemGroup={confirmLineItemGroup}
              onSetLineItemFieldOverride={setLineItemFieldOverride}
              onAddLineItem={addLineItem}
              onDuplicateLineItem={duplicateLineItem}
              onRemoveLineItem={removeLineItem}
              onMoveLineItem={moveLineItem}
              onApplyRecommendation={applyRecommendation}
              onConfirmFieldGroup={confirmFieldGroup}
              onSave={saveRecordById}
              onAutoSave={saveRecordById}
              onMarkComplete={markDocumentComplete}
            />
          ) : selectedSourceRecord ? (
            <SourceDocumentViewer
              record={selectedSourceRecord}
              linkedStructuredRecord={
                selectedSourceRecord.linkedStructuredDocumentId
                  ? visibleStructuredDocuments.find((record) => record.id === selectedSourceRecord.linkedStructuredDocumentId)
                  : undefined
              }
              onOpenLinkedStructuredRecord={(recordId) => handleSelect({ kind: 'structured', id: recordId })}
              onRemoveSource={() => void removeSourceDocumentsByIds([selectedSourceRecord.id])}
              removalBusy={removalBusy}
            />
          ) : (
            <div className="rounded-lg border border-dashed border-neutral-300 bg-neutral-50 p-8 text-sm text-neutral-600">
              Select a source document or structured record to begin.
            </div>
          )}
        </main>
      </div>
    </div>
  )
}

function RequiredDocumentsPanel({
  structuredDocuments,
  sourceDocuments,
  visibleDocumentTypes,
  selected,
  selectedSourceIds,
  removalBusy,
  removalMessage,
  removalError,
  onOpenRecord,
  onOpenSource,
  onToggleSourceSelection,
  onRemoveSource,
  onRemoveSelected,
  onCreateRecord,
}: {
  structuredDocuments: StructuredDocumentRecord[]
  sourceDocuments: SourceDocumentRecord[]
  visibleDocumentTypes: SupportedDocumentType[]
  selected: NavigatorTarget
  selectedSourceIds: string[]
  removalBusy: boolean
  removalMessage: string | null
  removalError: string | null
  onOpenRecord: (recordId: string) => void
  onOpenSource: (sourceId: string) => void
  onToggleSourceSelection: (sourceId: string, checked: boolean) => void
  onRemoveSource: (sourceId: string) => void
  onRemoveSelected: () => void
  onCreateRecord: (documentType: SupportedDocumentType) => void
}) {
  const unlinkedSourceDocuments = sourceDocuments.filter((record) => !record.mirrorsDocumentType)
  const visibleDefinitions = visibleDocumentTypes.map((documentType) => DOCUMENT_DEFINITIONS[documentType])
  const showSourceDocuments = sourceDocuments.length > 0

  return (
    <section className="rounded-lg border border-neutral-200 bg-white p-3 shadow-sm">
      <SectionHeading
        eyebrow="Required documents"
        title="Required documents"
        helpText="Use this workspace to complete the structured document record that supports the entry summary for this case."
        action={<FileStack size={16} className="text-secondary" aria-hidden="true" />}
      />
      {showSourceDocuments && (
        <>
          <div className="mt-3 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-neutral-200 bg-neutral-50 px-3 py-2.5">
            <div className="text-sm text-neutral-700">
              {selectedSourceIds.length > 0
                ? `${selectedSourceIds.length} uploaded file${selectedSourceIds.length === 1 ? '' : 's'} selected`
                : 'Select uploaded files to remove one or more mistaken uploads.'}
            </div>
            <button
              type="button"
              onClick={onRemoveSelected}
              disabled={selectedSourceIds.length === 0 || removalBusy}
              className="inline-flex items-center gap-2 rounded border border-error px-3 py-1.5 text-xs font-semibold text-error hover:bg-error/5 disabled:cursor-not-allowed disabled:border-neutral-200 disabled:text-neutral-400"
            >
              <Trash2 size={14} aria-hidden="true" />
              {removalBusy ? 'Removing…' : `Remove selected${selectedSourceIds.length > 0 ? ` (${selectedSourceIds.length})` : ''}`}
            </button>
          </div>
          {removalMessage && (
            <div className="mt-3 rounded border border-success/20 bg-success/5 px-3 py-2 text-sm text-success">
              {removalMessage}
            </div>
          )}
          {removalError && (
            <div className="mt-3 rounded border border-error/20 bg-error/5 px-3 py-2 text-sm text-error">
              {removalError}
            </div>
          )}
        </>
      )}
      <ul className="mt-3 space-y-2.5">
        {visibleDefinitions.map((definition) => {
          const record = structuredDocuments.find((item) => item.documentType === definition.type)
          const linkedSources = sourceDocuments.filter((item) => item.mirrorsDocumentType === definition.type)
          const isRecordSelected = record
            ? selected.kind === 'structured' && selected.id === record.id
            : false
          return (
            <li key={definition.type} className="rounded-lg border border-neutral-200 p-3">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <div className="text-sm font-semibold text-primary">{definition.label}</div>
                    <InlineHelp title={`${definition.label} help`} text={definition.shortDescription} />
                  </div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    <StatusPill
                      label={definition.requiredForReview ? 'required' : 'optional'}
                      tone={definition.requiredForReview ? 'info' : 'neutral'}
                    />
                    {record && (
                      <StatusPill label={record.status.replace(/_/g, ' ')} tone={toneForDocumentStatus(record.status)} />
                    )}
                    {linkedSources.length > 0 && (
                      <StatusPill
                        label={`${linkedSources.length} uploaded file${linkedSources.length === 1 ? '' : 's'}`}
                        tone="neutral"
                      />
                    )}
                  </div>
                </div>
                {record ? (
                  <button
                    type="button"
                    onClick={() => onOpenRecord(record.id)}
                    className="rounded border border-secondary px-3 py-1.5 text-xs font-semibold text-secondary hover:bg-secondary-lightest"
                  >
                    Open form
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => onCreateRecord(definition.type)}
                    className="rounded border border-secondary px-3 py-1.5 text-xs font-semibold text-secondary hover:bg-secondary-lightest"
                  >
                    Create form
                  </button>
                )}
              </div>
              {isRecordSelected && (
                <div className="mt-3 rounded border border-secondary/20 bg-secondary-lightest px-3 py-2 text-xs font-medium text-primary">
                  Editing this structured record now.
                </div>
              )}
              {showSourceDocuments && (
                <div className="mt-3 rounded-lg border border-neutral-200 bg-neutral-50 p-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wider text-neutral-500">
                    Uploaded files for this document
                  </div>
                  {linkedSources.length > 0 ? (
                    <ul className="mt-3 space-y-2">
                      {linkedSources.map((source) => {
                        const isSelected = selected.kind === 'source' && selected.id === source.id
                        return (
                          <li key={source.id}>
                            <div
                              className={`rounded border px-3 py-2 ${
                                isSelected
                                  ? 'border-secondary bg-white'
                                  : 'border-neutral-200 bg-white'
                              }`}
                            >
                              <div className="flex items-start gap-3">
                                <input
                                  type="checkbox"
                                  className="mt-1 h-4 w-4 rounded border-neutral-300 text-secondary focus:ring-secondary"
                                  checked={selectedSourceIds.includes(source.id)}
                                  onChange={(event) => onToggleSourceSelection(source.id, event.target.checked)}
                                  aria-label={`Select ${source.title}`}
                                />
                                <button
                                  type="button"
                                  onClick={() => onOpenSource(source.id)}
                                  className="min-w-0 flex-1 text-left"
                                >
                                  <div className="flex items-start justify-between gap-3">
                                    <div>
                                      <div className="text-sm font-semibold text-primary">{source.title}</div>
                                      <div className="mt-1 text-xs text-neutral-500">
                                        {source.autoPopulationStatus.replace(/_/g, ' ')}
                                        {source.autoPopulatedFieldCount > 0
                                          ? ` · ${source.autoPopulatedFieldCount} field${source.autoPopulatedFieldCount === 1 ? '' : 's'} added`
                                          : ''}
                                      </div>
                                    </div>
                                    <StatusPill
                                      label={source.status.replace(/_/g, ' ')}
                                      tone={toneForDocumentStatus(source.status)}
                                    />
                                  </div>
                                </button>
                                <button
                                  type="button"
                                  onClick={() => onRemoveSource(source.id)}
                                  disabled={removalBusy}
                                  className="inline-flex items-center gap-1 rounded border border-error px-2 py-1 text-[11px] font-semibold text-error hover:bg-error/5 disabled:cursor-not-allowed disabled:border-neutral-200 disabled:text-neutral-400"
                                >
                                  <Trash2 size={12} aria-hidden="true" />
                                  Remove
                                </button>
                              </div>
                            </div>
                          </li>
                        )
                      })}
                    </ul>
                  ) : (
                    <p className="mt-2 text-sm text-neutral-600">
                      No uploaded files are linked to this document yet.
                    </p>
                  )}
                </div>
              )}
            </li>
          )
        })}
      </ul>
      {showSourceDocuments && unlinkedSourceDocuments.length > 0 && (
        <div className="mt-3 rounded-lg border border-neutral-200 bg-white p-3">
          <div className="flex items-center gap-2">
            <div className="text-xs font-semibold uppercase tracking-wider text-neutral-500">
              Other uploaded files
            </div>
            <InlineHelp text="These files are attached to the case but are not yet matched to a required document record." />
          </div>
          <ul className="mt-3 space-y-2">
            {unlinkedSourceDocuments.map((source) => {
              const isSelected = selected.kind === 'source' && selected.id === source.id
              return (
                <li key={source.id}>
                  <div
                    className={`rounded border px-3 py-2 ${
                      isSelected
                        ? 'border-secondary bg-secondary-lightest'
                        : 'border-neutral-200 bg-white'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        className="mt-1 h-4 w-4 rounded border-neutral-300 text-secondary focus:ring-secondary"
                        checked={selectedSourceIds.includes(source.id)}
                        onChange={(event) => onToggleSourceSelection(source.id, event.target.checked)}
                        aria-label={`Select ${source.title}`}
                      />
                      <button
                        type="button"
                        onClick={() => onOpenSource(source.id)}
                        className="min-w-0 flex-1 text-left"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <div className="text-sm font-semibold text-primary">{source.title}</div>
                            <div className="mt-1 text-xs text-neutral-500">{source.documentTypeLabel}</div>
                          </div>
                          <StatusPill
                            label={source.status.replace(/_/g, ' ')}
                            tone={toneForDocumentStatus(source.status)}
                          />
                        </div>
                      </button>
                      <button
                        type="button"
                        onClick={() => onRemoveSource(source.id)}
                        disabled={removalBusy}
                        className="inline-flex items-center gap-1 rounded border border-error px-2 py-1 text-[11px] font-semibold text-error hover:bg-error/5 disabled:cursor-not-allowed disabled:border-neutral-200 disabled:text-neutral-400"
                      >
                        <Trash2 size={12} aria-hidden="true" />
                        Remove
                      </button>
                    </div>
                  </div>
                </li>
              )
            })}
          </ul>
        </div>
      )}
    </section>
  )
}

function WorkspaceUploadPanel({
  caseId,
  onUploaded,
  selectedStructuredRecord,
  selectedSourceRecord,
  linkedStructuredRecord,
}: {
  caseId: string
  onUploaded: () => void | Promise<void>
  selectedStructuredRecord?: StructuredDocumentRecord
  selectedSourceRecord?: SourceDocumentRecord
  linkedStructuredRecord?: StructuredDocumentRecord
}) {
  const contextRecord = selectedStructuredRecord ?? linkedStructuredRecord
  const helperCopy = contextRecord
    ? `Add source or supporting files for ${contextRecord.title} before continuing with document review and field updates.`
    : selectedSourceRecord
      ? 'Add another source or supporting file for this case. Uploaded files remain separate from the structured record and can be linked after upload.'
      : 'Add source or supporting files for the case before reviewing or editing the structured document fields below.'

  return (
    <section className="rounded-lg border border-neutral-200 bg-white p-4 shadow-sm">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <SectionHeading
          eyebrow="Upload supporting files"
          title="Upload supporting files"
          helpText={helperCopy}
        />
        {contextRecord && (
          <div className="rounded-lg border border-secondary/20 bg-secondary-lightest px-3 py-2 text-xs font-medium text-primary">
            Working against {contextRecord.title}
          </div>
        )}
      </div>

      <div className="mt-3 rounded-lg border border-neutral-200 bg-neutral-50 p-3">
        <DocumentUpload caseId={caseId} onUploaded={onUploaded} />
      </div>
    </section>
  )
}

function StructuredDocumentEditor({
  record,
  focusTarget,
  busy,
  saveMessage,
  onFieldChange,
  onFieldNoteChange,
  onConfirmField,
  onSetFieldOverride,
  onSectionNoteChange,
  onMarkSectionComplete,
  onLineItemFieldChange,
  onLineItemNoteChange,
  onConfirmLineItemField,
  onConfirmLineItemGroup,
  onSetLineItemFieldOverride,
  onAddLineItem,
  onDuplicateLineItem,
  onRemoveLineItem,
  onMoveLineItem,
  onApplyRecommendation,
  onConfirmFieldGroup,
  onSave,
  onAutoSave,
  onMarkComplete,
}: {
  record: StructuredDocumentRecord
  focusTarget: DocumentWorkspaceFocusTarget | null
  busy: boolean
  saveMessage: string | null
  onFieldChange: (recordId: string, fieldKey: string, nextValue: string) => void
  onFieldNoteChange: (recordId: string, fieldKey: string, note: string) => void
  onConfirmField: (recordId: string, fieldKey: string) => void
  onSetFieldOverride: (recordId: string, fieldKey: string, enabled: boolean, reason: string) => void
  onSectionNoteChange: (recordId: string, sectionId: string, note: string) => void
  onMarkSectionComplete: (recordId: string, sectionId: string) => void
  onLineItemFieldChange: (recordId: string, lineItemId: string, fieldKey: string, nextValue: string) => void
  onLineItemNoteChange: (recordId: string, lineItemId: string, note: string) => void
  onConfirmLineItemField: (recordId: string, lineItemId: string, fieldKey: string) => void
  onConfirmLineItemGroup: (recordId: string, lineItemId: string, fieldKeys: string[]) => void
  onSetLineItemFieldOverride: (
    recordId: string,
    lineItemId: string,
    fieldKey: string,
    enabled: boolean,
    reason: string,
  ) => void
  onAddLineItem: (recordId: string) => void
  onDuplicateLineItem: (recordId: string, lineItemId: string) => void
  onRemoveLineItem: (recordId: string, lineItemId: string) => void
  onMoveLineItem: (recordId: string, lineItemId: string, direction: 'up' | 'down') => void
  onApplyRecommendation: (recordId: string, recommendationId: string) => void
  onConfirmFieldGroup: (recordId: string, fieldKeys: string[]) => void
  onSave: (recordId: string) => Promise<void>
  onAutoSave: (recordId: string, options?: { auto?: boolean }) => Promise<void>
  onMarkComplete: (recordId: string) => void
}) {
  if (record.documentType === 'cbp_7501') {
    return (
      <CBP7501Editor
        record={record}
        focusTarget={focusTarget}
        busy={busy}
        saveMessage={saveMessage}
        onFieldChange={onFieldChange}
        onFieldNoteChange={onFieldNoteChange}
        onSetFieldOverride={onSetFieldOverride}
        onSectionNoteChange={onSectionNoteChange}
        onMarkSectionComplete={onMarkSectionComplete}
        onLineItemFieldChange={onLineItemFieldChange}
        onLineItemNoteChange={onLineItemNoteChange}
        onConfirmLineItemGroup={onConfirmLineItemGroup}
        onAddLineItem={onAddLineItem}
        onDuplicateLineItem={onDuplicateLineItem}
        onRemoveLineItem={onRemoveLineItem}
        onMoveLineItem={onMoveLineItem}
        onApplyRecommendation={onApplyRecommendation}
        onConfirmFieldGroup={onConfirmFieldGroup}
        onAutoSave={onAutoSave}
        onMarkComplete={onMarkComplete}
      />
    )
  }
  if (record.documentType === 'manifest') {
    return (
      <Manifest1302Editor
        record={record}
        focusTarget={focusTarget}
        busy={busy}
        saveMessage={saveMessage}
        onFieldChange={onFieldChange}
        onFieldNoteChange={onFieldNoteChange}
        onConfirmField={onConfirmField}
        onSetFieldOverride={onSetFieldOverride}
        onSectionNoteChange={onSectionNoteChange}
        onMarkSectionComplete={onMarkSectionComplete}
        onLineItemFieldChange={onLineItemFieldChange}
        onLineItemNoteChange={onLineItemNoteChange}
        onConfirmLineItemField={onConfirmLineItemField}
        onSetLineItemFieldOverride={onSetLineItemFieldOverride}
        onAddLineItem={onAddLineItem}
        onDuplicateLineItem={onDuplicateLineItem}
        onRemoveLineItem={onRemoveLineItem}
        onMoveLineItem={onMoveLineItem}
        onApplyRecommendation={onApplyRecommendation}
        onSave={onSave}
        onAutoSave={onAutoSave}
        onMarkComplete={onMarkComplete}
      />
    )
  }
  if (record.documentType === 'commercial_invoice') {
    return (
      <CommercialInvoiceEditor
        record={record}
        focusTarget={focusTarget}
        busy={busy}
        saveMessage={saveMessage}
        onFieldChange={onFieldChange}
        onFieldNoteChange={onFieldNoteChange}
        onConfirmField={onConfirmField}
        onSectionNoteChange={onSectionNoteChange}
        onMarkSectionComplete={onMarkSectionComplete}
        onLineItemFieldChange={onLineItemFieldChange}
        onLineItemNoteChange={onLineItemNoteChange}
        onConfirmLineItemField={onConfirmLineItemField}
        onAddLineItem={onAddLineItem}
        onDuplicateLineItem={onDuplicateLineItem}
        onRemoveLineItem={onRemoveLineItem}
        onMoveLineItem={onMoveLineItem}
        onApplyRecommendation={onApplyRecommendation}
        onSave={onSave}
        onAutoSave={onAutoSave}
        onMarkComplete={onMarkComplete}
      />
    )
  }

  const definition = DOCUMENT_DEFINITIONS[record.documentType]
  const issueMap = record.validationIssues.reduce<Record<string, string[]>>((acc, issue) => {
    acc[issue.fieldKey] = [...(acc[issue.fieldKey] ?? []), issue.message]
    return acc
  }, {})
  const validationSummary = summarizeValidationIssues(record.validationIssues)
  const recommendationMap = record.awaRecommendations.reduce<Record<string, StructuredDocumentRecord['awaRecommendations']>>((acc, recommendation) => {
    acc[recommendation.fieldKey] = [...(acc[recommendation.fieldKey] ?? []), recommendation]
    return acc
  }, {})

  return (
    <div className="space-y-3">
      <section className="bg-white border border-neutral-200 rounded-lg shadow-sm p-4">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div className="min-w-0">
            <SectionHeading eyebrow={definition.label} title={record.title} helpText={definition.shortDescription} />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <StatusPill label={record.status.replace(/_/g, ' ')} tone={toneForDocumentStatus(record.status)} />
            <div className="rounded border border-neutral-200 bg-neutral-50 px-4 py-2 text-sm text-neutral-600">
              <span className="font-semibold text-neutral-700">Last saved:</span>{' '}
              <span>{formatDateTime(record.lastUpdatedAt)}</span>
            </div>
            <button
              type="button"
              onClick={() => onMarkComplete(record.id)}
              className="inline-flex items-center gap-2 rounded bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary-dark"
            >
              <CheckCircle2 size={16} aria-hidden="true" />
              Mark complete
            </button>
          </div>
        </div>

        <div className="mt-3 grid gap-3 lg:grid-cols-[minmax(0,1fr)_18rem]">
          <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-3 text-sm text-neutral-700">
            <div className="flex items-center gap-2">
              <div className="font-semibold text-primary">ACE alignment note</div>
              <InlineHelp text={definition.aceReferenceNote} title="ACE alignment note" align="left" />
            </div>
          </div>
          <div className="rounded-lg border border-neutral-200 bg-white p-3">
            <div className="text-xs font-semibold uppercase tracking-wider text-neutral-500">Validation state</div>
            <dl className="mt-3 space-y-2 text-sm">
              <div className="flex items-center justify-between gap-3">
                <dt className="text-neutral-500">Blocking issues</dt>
                <dd className="font-semibold text-error">{validationSummary.errors}</dd>
              </div>
              <div className="flex items-center justify-between gap-3">
                <dt className="text-neutral-500">Warnings</dt>
                <dd className="font-semibold text-warning">{validationSummary.warnings}</dd>
              </div>
              <div className="flex items-center justify-between gap-3">
                <dt className="text-neutral-500">Last updated</dt>
                <dd className="font-medium text-primary">{formatDateTime(record.lastUpdatedAt)}</dd>
              </div>
              <div className="flex items-center justify-between gap-3">
                <dt className="text-neutral-500">AWA recommendations</dt>
                <dd className="font-medium text-warning">{record.awaRecommendations.length}</dd>
              </div>
            </dl>
          </div>
        </div>
        {busy && saveMessage && <p className="mt-3 text-sm text-success">{saveMessage}</p>}
      </section>

      {definition.sections.map((section) => (
        <section key={section.id} className="bg-white border border-neutral-200 rounded-lg shadow-sm p-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-2">
                <h4 className="text-base font-bold text-primary">{section.title}</h4>
                {section.description && (
                  <InlineHelp text={section.description} title={`${section.title} help`} align="left" />
                )}
              </div>
            </div>
          </div>
          <div className="mt-3 grid gap-3 md:grid-cols-2">
            {section.fields.map((field) => {
              const fieldState = record.fields[field.key]
              const messages = issueMap[field.key] ?? []
              const recommendations = recommendationMap[field.key] ?? []
              const isFocused = focusTarget?.documentId === record.id && focusTarget.fieldKey === field.key
              return (
                <label
                  key={field.key}
                  className={`block rounded-lg p-3 ${field.type === 'textarea' ? 'md:col-span-2' : ''} ${isFocused ? 'border border-accent-sunset bg-accent-sunset/5' : 'border border-neutral-200/60'}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="flex items-center gap-2 text-sm font-semibold text-primary">
                      <span>{field.label}</span>
                      {field.helpText && <InlineHelp text={field.helpText} title={`${field.label} help`} align="left" />}
                    </span>
                    <span className="text-[11px] text-neutral-500">
                      {field.aceMappingId ?? 'TODO mapping'}
                    </span>
                  </div>
                  <FieldControl
                    fieldType={field.type}
                    value={fieldState?.value ?? ''}
                    options={field.options}
                    placeholder={field.placeholder}
                    onChange={(value) => onFieldChange(record.id, field.key, value)}
                  />
                  <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-neutral-500">
                    <span>Origin: {(fieldState?.origin ?? 'manual').replace(/_/g, ' ')}</span>
                    <span>Editable: {field.editable === false ? 'No' : 'Yes'}</span>
                    {fieldState?.sourceValue && <span>Source value: {fieldState.sourceValue}</span>}
                    {fieldState?.sourceDocumentTitle && <span>Source document: {fieldState.sourceDocumentTitle}</span>}
                    {fieldState?.extractedFieldKey && <span>Mapped from: {fieldState.extractedFieldKey}</span>}
                  </div>
                  {fieldState?.conflictNote && (
                    <div className="mt-2 rounded border border-warning/30 bg-warning/10 p-2 text-xs text-neutral-700">
                      {fieldState.conflictNote}
                    </div>
                  )}
                  {recommendations.length > 0 && (
                    <div className="mt-3 space-y-2">
                      {recommendations.map((recommendation) => (
                        <div key={recommendation.id} className="rounded-lg border border-warning/30 bg-warning/10 p-3">
                          <div className="flex flex-wrap items-start justify-between gap-3">
                            <div>
                              <div className="text-sm font-semibold text-primary">{recommendation.message}</div>
                              <div className="mt-1 text-xs text-neutral-600">
                                Current: {recommendation.currentValue || '—'}
                                {recommendation.recommendedValue ? ` · Recommended: ${recommendation.recommendedValue}` : ''}
                              </div>
                              {recommendation.sourceEvidence && (
                                <div className="mt-1 text-xs text-neutral-600">
                                  Evidence: {recommendation.sourceEvidence}
                                </div>
                              )}
                              <div className="mt-1 text-xs text-neutral-500">
                                Linked finding: {recommendation.findingId}
                              </div>
                            </div>
                            <div className="flex flex-wrap items-center gap-2">
                              <StatusPill label={recommendation.state.replace(/_/g, ' ')} tone={toneForRecommendationState(recommendation.state)} />
                              {recommendation.recommendedValue && (
                                <button
                                  type="button"
                                  onClick={() => onApplyRecommendation(record.id, recommendation.id)}
                                  className="rounded border border-secondary px-2.5 py-1 text-xs font-semibold text-secondary hover:bg-secondary-lightest"
                                >
                                  Apply recommendation
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {messages.length > 0 && (
                    <ul className="mt-2 space-y-1 text-xs text-error">
                      {messages.map((message) => (
                        <li key={message}>{message}</li>
                      ))}
                    </ul>
                  )}
                </label>
              )
            })}
          </div>
        </section>
      ))}

      <section className="bg-white border border-neutral-200 rounded-lg shadow-sm p-5">
        <div className="text-xs font-semibold uppercase tracking-wider text-neutral-500">Audit and source provenance</div>
        <div className="mt-4 grid gap-4 lg:grid-cols-2">
          <div>
            <h4 className="text-sm font-semibold text-primary">Source links</h4>
            <ul className="mt-2 space-y-2 text-sm text-neutral-700">
              {record.sourceLinks.length === 0 ? (
                <li>No linked source documents yet.</li>
              ) : (
                record.sourceLinks.map((sourceId) => <li key={sourceId}>Linked source document: {sourceId}</li>)
              )}
            </ul>
          </div>
          <div>
            <h4 className="text-sm font-semibold text-primary">Recent audit activity</h4>
            <ul className="mt-2 space-y-2 text-sm text-neutral-700">
              {record.auditHistory.map((entry) => (
                <li key={entry.id}>
                  <span className="font-semibold text-primary">{entry.action}</span>
                  <span className="text-neutral-500"> · {formatDateTime(entry.at)}</span>
                  <p className="text-neutral-600">{entry.detail}</p>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>
    </div>
  )
}

function SourceDocumentViewer({
  record,
  linkedStructuredRecord,
  onOpenLinkedStructuredRecord,
  onRemoveSource,
  removalBusy,
}: {
  record: SourceDocumentRecord
  linkedStructuredRecord?: StructuredDocumentRecord
  onOpenLinkedStructuredRecord: (recordId: string) => void
  onRemoveSource: () => void
  removalBusy: boolean
}) {
  return (
    <div className="bg-white border border-neutral-200 rounded-lg shadow-sm p-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-neutral-500">
            Uploaded source document
          </div>
          <h3 className="mt-1 text-xl font-bold text-primary">{record.title}</h3>
          <p className="mt-2 text-sm text-neutral-600">
            Source documents remain distinct from structured records. They support document review, provenance, and future AWA extraction workflows.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            {record.mirrorsDocumentType && (
              <StatusPill
                label={`Linked to ${DOCUMENT_DEFINITIONS[record.mirrorsDocumentType].label}`}
                tone="info"
              />
            )}
            <StatusPill label={record.autoPopulationStatus.replace(/_/g, ' ')} tone={toneForSourceAutomation(record.autoPopulationStatus)} />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <StatusPill label={record.status.replace(/_/g, ' ')} tone={toneForDocumentStatus(record.status)} />
          <button
            type="button"
            onClick={onRemoveSource}
            disabled={removalBusy}
            className="inline-flex items-center gap-1 rounded border border-error px-3 py-1.5 text-xs font-semibold text-error hover:bg-error/5 disabled:cursor-not-allowed disabled:border-neutral-200 disabled:text-neutral-400"
          >
            <Trash2 size={12} aria-hidden="true" />
            {removalBusy ? 'Removing…' : 'Remove file'}
          </button>
        </div>
      </div>

      {linkedStructuredRecord && (
        <div className="mt-5 rounded-lg border border-secondary-light/30 bg-secondary-lightest p-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-neutral-500">
                Editable structured record
              </div>
              <div className="mt-1 text-base font-bold text-primary">{linkedStructuredRecord.title}</div>
              <p className="mt-1 text-sm text-neutral-600">
                This source artifact remains separate. Continue document completion in the linked structured form.
              </p>
            </div>
            <button
              type="button"
              onClick={() => onOpenLinkedStructuredRecord(linkedStructuredRecord.id)}
              className="inline-flex items-center justify-center rounded bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary-dark"
            >
              Open linked structured record
            </button>
          </div>
        </div>
      )}

      <dl className="mt-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-3 text-sm">
        <MetadataItem label="Document type" value={record.documentTypeLabel} />
        <MetadataItem label="Mime type" value={record.mimeType} />
        <MetadataItem label="File size" value={record.sizeLabel} />
        <MetadataItem label="Uploaded at" value={formatDateTime(record.uploadedAt)} />
        <MetadataItem label="Uploaded by" value={record.uploadedBy} />
        <MetadataItem
          label="Classification"
          value={`${record.documentTypeLabel} via ${record.classificationMethod.replace(/_/g, ' ')}`}
        />
        <MetadataItem
          label="Auto populated fields"
          value={`${record.autoPopulatedFieldCount}`}
        />
        <MetadataItem
          label="Conflicts preserved"
          value={`${record.conflictFieldCount}`}
        />
        <MetadataItem
          label="Text extraction"
          value={
            record.extractionStatus === 'done' && record.extractionConfidence != null
              ? `${Math.round(record.extractionConfidence * 100)}% confidence`
              : record.extractionStatus.replace(/_/g, ' ')
          }
        />
      </dl>

      {linkedStructuredRecord && (
        <div className="mt-6 rounded-lg border border-neutral-200 bg-neutral-50 p-4">
          <div className="text-xs font-semibold uppercase tracking-wider text-neutral-500">
            Structured record sync summary
          </div>
          <ul className="mt-3 space-y-2 text-sm text-neutral-700">
            <li>Linked structured form: {linkedStructuredRecord.title}</li>
            <li>Auto populated fields: {record.autoPopulatedFieldCount}</li>
            <li>Conflicts preserved for review: {record.conflictFieldCount}</li>
          </ul>
        </div>
      )}
    </div>
  )
}
